import { z } from 'zod';
import { PreviewUrlSchema } from './message-types.schema.js';

/**
 * Schema for thread status config information
 * Contains project configuration details
 */
export const ThreadStatusConfigSchema = z.object({
  projectName: z.string().optional().describe('Project name for the thread, if set'),
  packageName: z.string().optional().describe('Package name for the thread, if set'),
});

export type ThreadStatusConfig = z.infer<typeof ThreadStatusConfigSchema>;

/**
 * Schema for thread status preview information
 * Contains preview URL and status
 */
export const ThreadStatusPreviewSchema = PreviewUrlSchema.extend({
  id: z.string().describe('Unique ID for the preview'),
});

export type ThreadStatusPreview = z.infer<typeof ThreadStatusPreviewSchema>;

/**
 * Schema for thread status job information
 * Contains job progress status and job ID if in progress
 */
export const ThreadStatusJobSchema = z.discriminatedUnion('isInProgress', [
  z.object({
    isInProgress: z.literal(true).describe('Job is currently in progress'),
    jobId: z.string().describe('Job ID of the currently running job'),
    startedAt: z.number().describe('Unix timestamp (in milliseconds) when the job started'),
  }),
  z.object({
    isInProgress: z.literal(false).describe('No job is currently in progress'),
    jobId: z.undefined().optional(),
    startedAt: z.undefined().optional(),
  }),
]);

export type ThreadStatusJob = z.infer<typeof ThreadStatusJobSchema>;

/**
 * Schema for thread status response
 * Contains project metadata and job progress information
 */
export const ThreadStatusResponseSchema = z.object({
  config: ThreadStatusConfigSchema.describe('Project configuration information'),
  preview: ThreadStatusPreviewSchema.describe('Preview information'),
  job: ThreadStatusJobSchema.describe('Job progress information'),
});

export type ThreadStatusResponse = z.infer<typeof ThreadStatusResponseSchema>;

/**
 * Schema for thread status error response
 */
export const ThreadStatusErrorResponseSchema = z.object({
  error: z.string().describe('Error message describing what went wrong'),
});

export type ThreadStatusErrorResponse = z.infer<typeof ThreadStatusErrorResponseSchema>;
